/**
 * Assets Service
 * All Supabase interactions for the assets table.
 * Components should call these functions — never query Supabase directly.
 */

import { supabase } from '../supabaseClient';
import { mapAsset } from '../lib/helpers';

// Convert app fields → snake_case DB columns (handles both camelCase and snake_case input)
const toDb = (asset, includeId = true) => {
  const row = {
    name:                  asset.name,
    type:                  asset.type,
    epc:                   asset.epc,
    inventory_number:      asset.inventoryNumber      || asset.inventory_number,
    status:                asset.status,
    condition:             asset.condition,
    current_facility:      asset.currentFacility      || asset.current_facility      || asset.facility_id,
    current_facility_name: asset.currentFacilityName  || asset.current_facility_name,
    last_seen:             asset.lastSeen              || asset.last_seen,
    checked_out_to:        asset.checkedOutTo          || asset.checked_out_to,
    value:                 asset.value,
    vendor:                asset.vendor,
    brand:                 asset.brand,
    model:                 asset.model,
    serial_no:             asset.serial_no,
    purchase_date:         asset.purchaseDate          || asset.purchase_date,
    warranty_expiry:       asset.warrantyExpiry        || asset.warranty_expiry,
    notes:                 asset.notes,
    org_id:                asset.org_id,
  };
  if (includeId && asset.id) row.id = asset.id;
  return row;
};

export const assetsService = {

  async getAll() {
    const { data, error } = await supabase
      .from('assets')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data.map(mapAsset);
  },

  async getById(id) {
    const { data, error } = await supabase
      .from('assets')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return mapAsset(data);
  },

  async create(asset) {
    const { data, error } = await supabase
      .from('assets')
      .insert([toDb(asset, false)])   // false = don't include id, let DB generate
      .select()
      .single();
    if (error) throw error;
    return mapAsset(data);
  },

  async update(id, changes) {
    const { data, error } = await supabase
      .from('assets')
      .update(toDb(changes))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapAsset(data);
  },

  async delete(id) {
    const { error } = await supabase
      .from('assets')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  async updateFacility(assetId, facilityId, facilityName) {
    return this.update(assetId, {
      currentFacility:     facilityId,
      currentFacilityName: facilityName,
      status:              'Available',
      lastSeen:            new Date().toISOString(),
    });
  },
};
